import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import type { DunningPromptPreviewResponse, DunningPromptPreviewResult } from "../types"

const PREVIEW_URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_system_prompt_versions/preview`

export type PromptPreviewParams = {
  dunningCaseId: number
  /** 保存前の編集中プロンプト本文をそのまま試す場合に指定 */
  content?: string
  model?: string
  /** 保存済みバージョンを試す場合に指定 */
  promptVersionId?: number
}

type ApiError = {
  response?: { status?: number; data?: { message?: string } }
}

const previewErrorMessage = (e: unknown): string => {
  const response = (e as ApiError)?.response
  if (response?.status === 404) {
    return "指定したケースIDの督促ケースが見つかりません。"
  }
  return response?.data?.message ?? "プレビューの実行に失敗しました。"
}

/**
 * 指定した督促ケースの実コンテキストでプロンプトをプレビュー実行する。
 * 試し生成し、生成文面とモデルに送られたユーザープロンプト全文を返す。
 * dry-run であり、ドラフトや生成履歴は一切保存されない。
 * 呼び出し側（エディタ / バージョン履歴の各行）ごとに独立した状態を持つ。
 */
export const useDunningPromptPreview = () => {
  const { postWithToken } = useSmoothApi()

  const [result, setResult] = useState<DunningPromptPreviewResult | null>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const runPreview = async (params: PromptPreviewParams): Promise<void> => {
    setIsPreviewing(true)
    setPreviewError(null)
    try {
      // リクエストキーは camelCase のまま（サーバ側がそのまま受け取る）
      const response = await postWithToken(PREVIEW_URL, params)
      setResult((response.data as DunningPromptPreviewResponse).data)
    } catch (e) {
      // 422: content未指定等 / 502: Vertex認証なし（ローカル等）→ サーバのmessageを表示
      setPreviewError(previewErrorMessage(e))
      setResult(null)
    } finally {
      setIsPreviewing(false)
    }
  }

  const clearPreview = () => {
    setResult(null)
    setPreviewError(null)
  }

  return { result, isPreviewing, previewError, runPreview, clearPreview }
}
